import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {distinctUntilChanged} from 'rxjs/operators';
import {DefaultFilter} from '@mominsamir/ngx-smart-table';
import {NbPopoverDirective, NbToastrService} from '@nebular/theme';
import {Subscription} from 'rxjs';

@Component({
    template: `
        <div class="position-relative">
            <div [nbPopover]="tabs" nbPopoverPlacement="bottom">
                <input
                    nbInput
                    fullWidth
                    #input
                    placeholder="min — max"
                    [class.active]="isPopoverShown"
                    [formControl]="inputControl"
                    id="input-box"
                />
                <div class="icon-wrapper fullwidth">
                    <nb-icon icon="chevron-down" status="basic"></nb-icon>
                </div>

                <div *ngIf="isPopoverShown && inputControl.value" class="icon-wrapper reset" (click)="reset()">
                    <nb-icon icon="close-outline" status="basic"></nb-icon>
                </div>
            </div>

            <ng-template #tabs>
                <nb-card
                    [style.min-width.px]="200"
                    [style.width.px]="input.getBoundingClientRect().width"
                    style="margin:0"
                >
                    <div class="d-flex flex-column">
                        <div class="flex">
                            <div class="form-field">
                                <label for="min" class="label">Min</label>
                                <input
                                    nbInput
                                    fullWidth
                                    fieldSize="small"
                                    type="number"
                                    id="min"
                                    autofocus
                                    [formControl]="minControl"
                                    (keydown.enter)="max.focus()"
                                    ngxOnlyNumber
                                    [integer]="isInteger"
                                />
                            </div>

                            <div class="form-field">
                                <label for="max" class="label">Max</label>
                                <input
                                    nbInput
                                    fullWidth
                                    fieldSize="small"
                                    type="number"
                                    id="max"
                                    #max
                                    [formControl]="maxControl"
                                    (keydown.enter)="popover.hide()"
                                    (blur)="onMaxBlur($event)"
                                    ngxOnlyNumber
                                    [integer]="isInteger"
                                />
                            </div>
                        </div>
                        <div>
                            <button
                                *ngIf="isPopoverShown && showClearButton()"
                                nbButton
                                status="basic"
                                size="small"
                                class="clear-button"
                                (click)="clearValues($event)"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </nb-card>
            </ng-template>
        </div>
    `,
    styleUrls: ['numeric-range-filter.component.scss'],
    styles: [
        `
            #input-box {
                white-space: nowrap;
                padding-right: 18px !important;
                text-overflow: ellipsis;
            }

            .flex {
                align-items: center;
                display: flex;
                justify-content: space-between;
                width: 100%;
                margin: 2px;
                padding: 5px;
            }

            .form-field {
                width: 47%;
                min-width: 82px;
                margin: 5px;
            }

            label {
                font-family: Roboto, 'Helvetica Neue', sans-serif !important;
                font-weight: 500 !important;
                margin-bottom: 0.25rem;
            }

            .clear-button {
                float: right;
                margin: 5px;
            }
        `,
    ],
})
export class NumericRangeFilterComponent extends DefaultFilter implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;
    isPopoverShown: boolean = false;

    inputControl: FormControl = new FormControl();
    minControl: FormControl = new FormControl();
    maxControl: FormControl = new FormControl();

    subscriptions: Subscription[];

    constructor(protected cd: ChangeDetectorRef, private toastrService: NbToastrService) {
        super();
    }

    private minVal: number;
    private checkMax: boolean;
    private maxVal: number;

    public isInteger: boolean = true;

    ngOnInit() {
        this.minVal = this.column?.filter?.config?.min || 0;
        this.checkMax = this.column?.filter?.config?.max != undefined;
        this.maxVal = this.column?.filter?.config?.max || 999999999;
        this.isInteger = this.column.filter.config?.isInteger ?? true;

        this.subscriptions = [
            this.minControl.valueChanges
                // .pipe(debounceTime(this.delay)) do we need this?
                .subscribe((value: number) => {
                    // Check value below defined min
                    if (value !== null && value < this.minVal) {
                        value = this.minVal;
                        this.minControl.setValue(value, {emitEvent: false});
                    }

                    // Check value above defined max, if a max is defined
                    if (value !== null && this.checkMax && value > this.maxVal) {
                        value = this.maxVal;
                        this.minControl.setValue(value, {emitEvent: false});
                    }

                    let min = value !== null ? value + '' : 'min',
                        max = this.maxControl.value !== null ? this.maxControl.value + '' : 'max';

                    if (value !== null && this.maxControl.value !== null && this.maxControl.value < value) {
                        this.maxControl.setValue(value, {emitEvent: false});
                        max = this.maxControl.value + '';
                    }

                    let result = value == null && this.maxControl.value == null ? '' : `${min} — ${max}`;
                    this.inputControl.setValue(result, {emitEvent: false});
                }),

            this.maxControl.valueChanges
                // .pipe(debounceTime(this.delay))  do we need this?
                .subscribe((value: number) => {
                    // Check value below defined min
                    if (value !== null && value < this.minVal) {
                        value = this.minVal;
                        this.maxControl.setValue(value, {emitEvent: false});
                    }

                    // Check value above defined max, if a max is defined
                    if (value !== null && this.checkMax && value > this.maxVal) {
                        value = this.maxVal;
                        this.maxControl.setValue(value, {emitEvent: false});
                    }

                    let min = this.minControl.value !== null ? this.minControl.value + '' : 'min',
                        max = value !== null ? value + '' : 'max';

                    if (value !== null && this.minControl.value !== null && this.minControl.value > value) {
                        min = this.minControl.value + '';
                    }

                    let result = value == null && this.minControl.value == null ? '' : `${min} — ${max}`;
                    this.inputControl.setValue(result, {emitEvent: false});
                }),

            this.inputControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value: string) => {
                if (value == null) {
                    this.query = '';
                    this.setFilter();
                } else if (this.minControl.value != null || this.maxControl.value != null) {
                    if (this.column.filter.config && this.column.filter.config.type) {
                        this.numberType(this.column.filter.config.type);
                    } else {
                        // Do not use '||' operator here, since real value of 0 is treated as "false" and will be ignored
                        this.query =
                            (this.minControl.value == null ? this.maxControl.value : this.minControl.value) +
                            '|' +
                            (this.maxControl.value == null ? this.minControl.value : this.maxControl.value);
                    }
                    this.setFilter();
                }
            }),
        ];
    }

    numberType(type) {
        switch (type) {
            case 'fraction':
                let min = (this.minControl.value === null ? this.maxControl.value : this.minControl.value) / 100;
                let max = (this.maxControl.value === null ? this.minControl.value : this.maxControl.value) / 100;
                this.query = min + '|' + max;
                break;
            default:
                break;
        }
    }

    ngAfterViewInit() {
        this.subscriptions.push(
            this.popover?.nbPopoverShowStateChange.subscribe(({isShown}) => {
                if (!this.popover.isShown) {
                    this.isPopoverShown = false;
                    this.completeValues();
                } else {
                    this.isPopoverShown = true;
                }
                this.cd.detectChanges();
            }),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.source) {
            this.minControl.reset();
            this.maxControl.reset();
        }
        if (changes.query) {
            this.query = changes.query.currentValue;
            this.inputControl.setValue(this.inputControl.value);
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    completeValues() {
        // stop making a request if the value is more than max number.
        const min = this.minControl.value;
        const max = this.maxControl.value;

        const exceedsMax = [min, max].some((v) => Number(v) > this.maxVal);
        const isSci = [min, max].some((v) => this.isScientificString(String(v)));

        if (exceedsMax || isSci) {
            this.reset();
            this.inputControl.reset();
            this.toastrService.danger(
                `Please enter less than ${this.maxVal} and more than ${this.minVal}`,
                'Too much number typed',
            );
            return;
        }

        if (this.minControl.value === null) this.minControl.setValue(this.maxControl.value);

        if (this.maxControl.value === null) this.maxControl.setValue(this.minControl.value);

        if (this.minControl.value === null && this.maxControl.value === null) {
            this.inputControl.reset();
        } else {
            let result = `${this.minControl.value} — ${this.maxControl.value}`;
            this.inputControl.setValue(result);
        }
    }

    reset() {
        this.minControl.reset();
        this.maxControl.reset();
    }

    clearValues(e: MouseEvent): void {
        this.reset();
        this.inputControl.reset();
        e.stopPropagation();
    }

    showClearButton(): boolean {
        const min = this.minControl.value;
        const max = this.maxControl.value;

        return (
            typeof min === 'number' ||
            typeof max === 'number' ||
            this.isScientificString(String(min)) ||
            this.isScientificString(String(max))
        );
    }

    onMaxBlur(event: FocusEvent) {
        let value = (event.target as HTMLTextAreaElement).value;
        let min = this.minControl.value !== null ? this.minControl.value + '' : 'min',
            max = value !== null ? value + '' : 'max';

        if (value !== null && this.minControl.value !== null && this.minControl.value > value) {
            this.minControl.setValue(value, {emitEvent: false});
            min = this.minControl.value + '';
        }

        let result = value == null && this.minControl.value == null ? '' : `${min} — ${max}`;
        this.inputControl.setValue(result, {emitEvent: false});
    }

    // RegExp for Scientific notation
    SCIENTIFIC_RE = /^\s*[+-]?(?:\d+\.?\d*|\.\d+)[eE][+-]?\d+\s*$/;

    private isScientificString(s: string): boolean {
        return this.SCIENTIFIC_RE.test(s);
    }
}
